import React, {
	Children,
	cloneElement,
	createContext,
	isValidElement,
	memo,
	useContext,
	useEffect,
	useState,
} from 'react';
import type { PropsWithChildren } from 'react';

import {
	defaultNodeComponents,
	ReactSerializer,
	type ReactSerializerInit,
} from '@atlaskit/renderer';

export class ProgressiveReactSerializer extends ReactSerializer {
	/**
	 * The first invalid index (after a sequence of valid indices) of a node to batch render.
	 * After `serializeFragment` completes for the root node, this will be the number of nodes to batch render.
	 */
	private maxRenderIndex: number;
	private batchSize: number;

	constructor(init: ReactSerializerInit, batchSize: number) {
		super(init);
		this.maxRenderIndex = 0;
		this.batchSize = batchSize;
	}

	/**
	 * @override
	 */
	serializeFragment(
		fragment: SerializeFragmentParameters[0],
		props?: SerializeFragmentParameters[1],
		_target?: SerializeFragmentParameters[2],
		key?: SerializeFragmentParameters[3],
		parentInfo?: SerializeFragmentParameters[4],
	): JSX.Element | null {
		const isRoot = key === undefined; // Like ReactSerializer.serializeFragment

		if (isRoot) {
			// Like ReactSerializer.resetState
			this.maxRenderIndex = 0;
		}

		const renderIndex = this.maxRenderIndex++;

		const nodeElement = super.serializeFragment.apply(
			this,
			arguments as unknown as SerializeFragmentParameters,
		);

		if (
			!nodeElement ||
			// Some node components rely on the type of children. Do not wrap such children.
			// For example, there is an expectation in the table component(s) in `isHeaderRowEnabled` of `TableHeader`.
			nodeElement.type === TableHeader ||
			// There is an expectation in the table component(s) in `isHeaderRowEnabled` about the children of the first row.
			isHeaderTableRow(fragment, props, parentInfo)
		) {
			return nodeElement;
		}

		return isRoot ? (
			<RootNodeRenderer maxRenderIndex={this.maxRenderIndex} batchSize={this.batchSize}>
				{nodeElement}
			</RootNodeRenderer>
		) : (
			<NodeRenderer key={key} renderIndex={renderIndex}>
				{nodeElement}
			</NodeRenderer>
		);
	}
}

function isHeaderTableRow(
	fragment: SerializeFragmentParameters[0],
	props: SerializeFragmentParameters[1],
	parentInfo: SerializeFragmentParameters[4],
) {
	try {
		if (props?.nodeType === 'tableRow' && parentInfo) {
			const parentPath = parentInfo?.path;
			if (parentPath?.length > 1) {
				const tableNode = parentPath[parentPath.length - 2];
				if (
					tableNode?.type?.name === 'table' &&
					parentPath[parentPath.length - 1]?.type?.name === 'tableRow' &&
					tableNode.maybeChild(0)?.content === fragment
				) {
					return true;
				}
			}
		}
	} catch (e) {
		if (process.env.NODE_ENV !== 'production') {
			throw e;
		}
	}
	return false;
}

const NodeRenderer = memo(({ children, renderIndex, ...props }: NodeRendererProps) => {
	const maxBatchRenderIndex = useContext(NodeRendererContext);
	const shouldRender = !maxBatchRenderIndex || renderIndex < maxBatchRenderIndex;

	if (!shouldRender) {
		return null;
	}

	// Some node components add props to their children, outside of `serializeFragment`. Pass such props through:
	if (Object.keys(props).length) {
		return Children.map(children, (child) =>
			isValidElement(child) ? cloneElement(child, props) : child,
		);
	}

	return children;
});

type NodeRendererProps = PropsWithChildren<{
	renderIndex: number;
}>;

const NodeRendererContext = createContext<number>(0);

const RootNodeRenderer = memo(({ children, maxRenderIndex, batchSize }: RootNodeRendererProps) => {
	const [batchIndex, setBatchIndex] = useState(0);

	const lastBatchIndex = Math.max(Math.ceil(maxRenderIndex / batchSize), 1) - 1; // The last valid batch index

	useEffect(() => {
		let cancelHandle: number | undefined;

		if (batchIndex < lastBatchIndex) {
			const requestIdleCallback = window.requestIdleCallback || window.requestAnimationFrame;
			cancelHandle = requestIdleCallback(() =>
				setBatchIndex((batchIndex) => (batchIndex < lastBatchIndex ? batchIndex + 1 : batchIndex)),
			);
		}

		return () => {
			if (cancelHandle !== undefined) {
				const cancelIdleCallback = window.cancelIdleCallback || window.cancelAnimationFrame;
				cancelIdleCallback(cancelHandle);
			}
		};
	}, [batchIndex, lastBatchIndex, setBatchIndex]);

	const maxBatchRenderIndex = (batchIndex + 1) * batchSize;

	return (
		<NodeRendererContext.Provider value={maxBatchRenderIndex}>
			{children}
		</NodeRendererContext.Provider>
	);
});

type RootNodeRendererProps = PropsWithChildren<{
	maxRenderIndex: number;
	batchSize: number;
}>;

type SerializeFragmentParameters = Parameters<ReactSerializer['serializeFragment']>;

const TableHeader = defaultNodeComponents?.tableHeader;
